import React, { useEffect, useRef, useState } from 'react';
import { apiUrl, parseApiResponse } from '../../services/api';
import { ONBOARDING_PATHS } from './onboardingPaths';

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

const AccountCreationModal = ({ open, onClose, googleSetup = null, onAuthenticated }) => {
  const [step, setStep] = useState(1);
  const [selectedPath, setSelectedPath] = useState('');
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(false);
  const closeButtonRef = useRef(null);

  const selectedDetails = ONBOARDING_PATHS.find((path) => path.key === selectedPath);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [loading, onClose, open]);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setSelectedPath('');
      setForm(INITIAL_FORM);
      setError('');
      setCreated(false);
    }
  }, [open]);

  if (!open) return null;

  const updateField = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const submitAccount = async (event) => {
    event.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(apiUrl('/api/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.firstName,
          last_name: form.lastName,
          email: form.email,
          password: form.password,
          onboarding_path: selectedPath,
        }),
      });
      await parseApiResponse(response);
      setCreated(true);
    } catch (requestError) {
      setError(requestError?.message || 'Unable to create your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const completeGoogleAccount = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await fetch(apiUrl('/api/auth/google/complete'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          setup_token: googleSetup.setupToken,
          onboarding_path: selectedPath,
        }),
      });
      const data = await parseApiResponse(response);
      onAuthenticated?.(data);
    } catch (requestError) {
      setError(requestError?.message || 'Unable to finish Google account setup.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="account-modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !loading && onClose()}>
      <section className="account-modal" role="dialog" aria-modal="true" aria-labelledby="account-modal-title">
        <header className="account-modal__header">
          {!created && (
            <div className="account-modal__progress" aria-label={`Account creation step ${step} of 2`}>
              <span>Step {step} of 2</span>
              <div><i style={{ width: `${step * 50}%` }} /></div>
            </div>
          )}
          <button ref={closeButtonRef} className="account-modal__close" type="button" onClick={onClose} disabled={loading} aria-label="Close account creation">
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        </header>

        {created ? (
          <div className="account-modal__success">
            <span className="account-modal__success-icon"><i className="pi pi-check" aria-hidden="true" /></span>
            <p className="account-modal__eyebrow">Account created</p>
            <h2 id="account-modal-title">Welcome to GravviSoft</h2>
            <p>Your {selectedDetails?.title} onboarding path is ready. Sign in to continue.</p>
            <button className="login-submit" type="button" onClick={onClose}>Continue to Sign In</button>
          </div>
        ) : step === 1 ? (
          <div className="account-modal__content">
            <p className="account-modal__eyebrow">Create your account</p>
            <h2 id="account-modal-title">How will you use GravviSoft?</h2>
            <p className="account-modal__intro">Choose where you would like to begin. You can add other approved roles to the same account later.</p>
            <div className="account-path-grid" role="radiogroup" aria-label="How you will use GravviSoft">
              {ONBOARDING_PATHS.map((path) => (
                <button
                  type="button"
                  role="radio"
                  aria-checked={selectedPath === path.key}
                  className={`account-path${selectedPath === path.key ? ' is-selected' : ''}`}
                  key={path.key}
                  onClick={() => setSelectedPath(path.key)}
                >
                  <span className="account-path__icon"><i className={`pi ${path.icon}`} aria-hidden="true" /></span>
                  <span><strong>{path.title}</strong><small>{path.description}</small></span>
                  <i className="pi pi-check account-path__check" aria-hidden="true" />
                </button>
              ))}
            </div>
            <div className="account-modal__actions">
              <button className="account-modal__secondary" type="button" onClick={onClose}>Cancel</button>
              <button className="account-modal__primary" type="button" disabled={!selectedPath || loading} onClick={googleSetup ? completeGoogleAccount : () => setStep(2)}>
                {loading ? 'Creating account…' : googleSetup ? 'Create Account' : 'Continue'} {!loading && <i className="pi pi-arrow-right" aria-hidden="true" />}
              </button>
            </div>
          </div>
        ) : (
          <form className="account-modal__content account-modal__form" onSubmit={submitAccount}>
            <p className="account-modal__eyebrow">{selectedDetails?.title}</p>
            <h2 id="account-modal-title">Create your sign-in</h2>
            <p className="account-modal__intro">This creates one GravviSoft account. Access is limited until any required application, invitation, or approval is complete.</p>
            <div className="login-row">
              <div className="login-field">
                <label htmlFor="signup-first-name">First name</label>
                <input id="signup-first-name" name="firstName" value={form.firstName} onChange={updateField} autoComplete="given-name" required autoFocus />
              </div>
              <div className="login-field">
                <label htmlFor="signup-last-name">Last name</label>
                <input id="signup-last-name" name="lastName" value={form.lastName} onChange={updateField} autoComplete="family-name" required />
              </div>
            </div>
            <div className="login-field">
              <label htmlFor="signup-email">Email</label>
              <input id="signup-email" name="email" type="email" value={form.email} onChange={updateField} autoComplete="email" placeholder="you@example.com" required />
            </div>
            <div className="login-row">
              <div className="login-field">
                <label htmlFor="signup-password">Password</label>
                <input id="signup-password" name="password" type="password" value={form.password} onChange={updateField} autoComplete="new-password" minLength="8" placeholder="At least 8 characters" required />
              </div>
              <div className="login-field">
                <label htmlFor="signup-confirm-password">Confirm password</label>
                <input id="signup-confirm-password" name="confirmPassword" type="password" value={form.confirmPassword} onChange={updateField} autoComplete="new-password" minLength="8" required />
              </div>
            </div>
            {error && <div className="account-modal__error" role="alert">{error}</div>}
            <div className="account-modal__actions">
              <button className="account-modal__secondary" type="button" onClick={() => { setError(''); setStep(1); }} disabled={loading}>
                <i className="pi pi-arrow-left" aria-hidden="true" /> Back
              </button>
              <button className="account-modal__primary" type="submit" disabled={loading}>
                {loading ? 'Creating account…' : 'Create Account'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
};

export default AccountCreationModal;

